import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
}                                 from '@angular/core';
import {
  AbstractControl,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
}                                 from '@angular/forms';
import { floor }                  from 'lodash';
import moment, { Moment }         from 'moment';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  takeUntil,
}                                 from 'rxjs';
import { BeneficiaryType }        from '~domain/enums';
import {
  Beneficiary,
  BeneficiaryFormData,
  CreditorRemittanceInformationType,
  PaymentAccount,
  PaymentFieldsToRetain,
  PaymentFormData,
  SimpleClient,
  supportPayments,
  toBeneficiaryFormData,
}                                 from '~domain/types';
import { RbForm }                 from '~shared/directives';
import { CustomValidators }       from '../core/validators';
import { EditBeneficiaryRequest } from './edit-beneficiary.component';

@Component({
  selector: 'rb-edit-payment',
  templateUrl: './edit-payment.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditPaymentComponent),
      multi: true,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => EditPaymentComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class EditPaymentComponent extends RbForm implements OnChanges, OnInit {
  @Input({ required: true })
  paymentClients: SimpleClient[] | null = [];
  @Input({ required: true })
  paymentAccounts: PaymentAccount[] | null = [];
  @Input({ required: true })
  beneficiaries: Beneficiary[] | null = [];
  @Input()
  addPaymentEnabled = true;
  @Input()
  beneficiariesLoading: boolean | null = false;
  @Input()
  addPaymentLoading: boolean | null = false;
  @Input()
  singleColumn: boolean = false;
  @Input()
  beneficiaryEditing: boolean | null = false;
  @Input()
  clientInputDisabled: boolean = false;
  @Input()
  predictedClient: SimpleClient | null = null;
  @Input()
  paymentFieldsToRetain: PaymentFieldsToRetain = {
    from: true,
    beneficiary: false,
    amount: false,
    remittanceInformation: false,
    internalNote: false,
    executionDate: false,
  };
  @Output()
  fetchClientBeneficiaries = new EventEmitter<string>;
  @Output()
  saveBeneficiary = new EventEmitter<EditBeneficiaryRequest>;
  @Output()
  paymentFieldsToRetainChange = new EventEmitter<PaymentFieldsToRetain>;

  override form = this.fb.group({
    clientId: this.fb.control(''),
    accountId: this.fb.control(''),
    beneficiary: this.fb.control<Beneficiary | null>({
      value: null,
      disabled: true,
    }, { validators: [this.beneficiaryValidator] }),
    amount: this.fb.control<number | null>(null),
    executionDate: this.fb.control<Moment | null>(null, { updateOn: 'blur' }),
    remittanceInformation: this.fb.control<string>(''),
    internalNote: this.fb.control<string | null>(null),
    remittanceInformationType: this.fb.control(CreditorRemittanceInformationType.unstructured),
    anotherPayment: this.fb.control(false),
    editBeneficiary: this.fb.control<BeneficiaryFormData | null>(null),
  }, {
    validators: [
      CustomValidators.remittanceInformationValidator('remittanceInformation', 'remittanceInformationType'),
    ],
  });
  paymentFieldsToRetainForm = this.fb.group({
    from: this.fb.control(true),
    beneficiary: this.fb.control(false),
    amount: this.fb.control(false),
    remittanceInformation: this.fb.control(false),
    internalNote: this.fb.control(false),
    executionDate: this.fb.control(false),
  });

  clientsSearch = this.fb.control('');
  filteredClients$ = new BehaviorSubject<SimpleClient[]>([]);
  accountsSearch = this.fb.control('');
  filteredAccounts$ = new BehaviorSubject<PaymentAccount[]>([]);
  beneficiariesSearch = this.fb.control('');
  filteredBeneficiaries$ = new BehaviorSubject<Beneficiary[]>([]);

  editBeneficiaryEnabled = false;
  editBeneficiaryConfirmed = false;
  beneficiaryToAdd: BeneficiaryFormData | null = null;
  protected readonly BeneficiaryType = BeneficiaryType;

  get selectedClient() {
    return this.paymentClients?.find(client => client.id === this.form.controls.clientId.value);
  }

  get selectedAccount() {
    return this.paymentAccounts?.find(account => account.id === this.form.controls.accountId.value);
  }

  get structured(): boolean {
    return this.form.controls.remittanceInformationType.value === CreditorRemittanceInformationType.structured;
  }

  get today() {
    return moment.utc(new Date()).startOf('day');
  }

  get oneYearFromToday() {
    return moment.utc(new Date()).add(1, 'year').subtract(1, 'day').startOf('day');
  }

  private get beneficiaryValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value === null || !control.value.counterpartName || !control.value.counterpartReference) {
        return { required: true };
      }
      if (control.value.counterpartReference === this.selectedAccount?.iban) {
        return { sameIban: true };
      }
      return null;
    };
  }

  private get beneficiary() {
    let matchingBeneficiary = this.beneficiaries?.find(beneficiary => beneficiary.counterpartName === this.form.controls.beneficiary.value?.counterpartName && beneficiary.counterpartReference === this.form.controls.beneficiary.value?.counterpartReference);
    if (!matchingBeneficiary) {
      matchingBeneficiary = this.beneficiaries?.find(beneficiary => beneficiary.counterpartReference === this.form.controls.beneficiary.value?.counterpartReference);
    }
    return matchingBeneficiary;
  }

  override validate(control: AbstractControl): Observable<ValidationErrors | null> {
    return super.validate(control)
      .pipe(map(errors => {
          if (errors) {
            return errors;
          }
          return this.editBeneficiaryEnabled ? { editing: true } : null;
        },
      ))
      ;
  }

  override ngOnInit() {
    super.ngOnInit();

    this.clientsSearch.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.filterClients(value));
    this.accountsSearch.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.filterAccounts(value));
    this.beneficiariesSearch.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.filterBeneficiaries(value));
    this.form.controls.amount.valueChanges
      .pipe(takeUntil(this.destroy$), filter(Boolean))
      .subscribe(() => this.limitAmountToTwoDecimals());
    this.paymentFieldsToRetainForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.paymentFieldsToRetainChange.emit(this.paymentFieldsToRetainForm.getRawValue()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paymentFieldsToRetain']) {
      this.paymentFieldsToRetainForm.setValue(this.paymentFieldsToRetain, { emitEvent: false });
    }

    if (this.beneficiariesLoading) {
      if (this.form.controls.beneficiary.enabled) {
        this.form.controls.beneficiary.disable();
      }
    } else if (this.form.controls.beneficiary.disabled) {
      this.form.controls.beneficiary.enable();
    }

    if (!this.beneficiaryEditing && this.editBeneficiaryConfirmed) {
      this.cancelEditBeneficiary();
    }
    if (!this.editBeneficiaryEnabled && this.beneficiary) {
      this.form.controls.beneficiary.setValue(this.beneficiary);
      this.form.controls.editBeneficiary.setValue(toBeneficiaryFormData(this.beneficiary));
    }

    if (changes['paymentClients']) {
      this.filterClients();
    }
    if (changes['paymentAccounts']) {
      this.filterAccounts();
    }
    if (changes['beneficiaries']) {
      this.filterBeneficiaries();
    }
  }

  override writeValue(data: PaymentFormData) {
    this.form.setValue(data);

    if (data.accountId !== '' && data.clientId === '' && this.selectedAccount) {
      this.form.controls.clientId.setValue(this.selectedAccount.clientId);
      this.fetchClientBeneficiaries.emit(this.form.controls.clientId.value);
    } else if (data.clientId === '' && this.predictedClient !== null && supportPayments(this.predictedClient)) {
      this.form.controls.clientId.setValue(this.predictedClient.id);
    }

    this.filterAccounts();
    if (this.form.controls.clientId.value !== '' && data.accountId === '') {
      this.selectFirstAccount();
    }
    this.limitAmountToTwoDecimals();
  }

  toggleRemittanceInformationType() {
    this.form.controls.remittanceInformationType.setValue(
      this.form.controls.remittanceInformationType.value === CreditorRemittanceInformationType.unstructured ?
        CreditorRemittanceInformationType.structured :
        CreditorRemittanceInformationType.unstructured,
    );
  }

  onClientChanged() {
    this.reloadBeneficiaries();
    this.filterAccounts();
    this.selectFirstAccount();
  }

  getBeneficiaryIcon(beneficiary: Beneficiary) {
    if (beneficiary.singleUse) {
      return 'counterpart-single-use';
    }
    if (beneficiary.id === null || !beneficiary.global) {
      return 'counterpart-client';
    }
    return 'counterpart-global';
  }

  onAccountChanged() {
    if (this.selectedAccount && this.selectedAccount.clientId !== this.form.controls.clientId.value) {
      this.form.controls.clientId.setValue(this.selectedAccount.clientId);
      this.reloadBeneficiaries();
    }
  }

  editBeneficiary() {
    this.editBeneficiaryEnabled = true;
    this.form.controls.clientId.disable();
    this.form.controls.beneficiary.markAsTouched();
    this.form.controls.beneficiary.clearValidators();
    this.form.controls.editBeneficiary.setValue(this.beneficiaryToAdd != null ? this.beneficiaryToAdd : toBeneficiaryFormData(this.form.controls.beneficiary.value));
    setTimeout(() => {
      this.form.controls.beneficiary.updateValueAndValidity();
    });
  }

  addBeneficiary() {
    if (this.beneficiaryToAdd == null) {
      this.beneficiaryToAdd = {
        counterpartName: this.beneficiariesSearch.value,
        counterpartReference: '',
        type: 'single-use',
      };
    }
    this.form.controls.beneficiary.setValue(null);
    this.beneficiariesSearch.setValue('');
    this.editBeneficiary();
  }

  cancelEditBeneficiary() {
    this.editBeneficiaryEnabled = false;
    this.editBeneficiaryConfirmed = false;
    this.beneficiaryToAdd = null;
    this.form.controls.clientId.enable();
    this.form.controls.beneficiary.markAsTouched();
    this.form.controls.beneficiary.setValidators([this.beneficiaryValidator]);
    setTimeout(() => {
      this.form.controls.beneficiary.updateValueAndValidity();
    });
  }

  confirmEditBeneficiary(beneficiary: Beneficiary) {
    this.editBeneficiaryConfirmed = true;
    this.beneficiaryToAdd = null;
    this.form.controls.beneficiary.setValue({
      ...beneficiary,
      id: beneficiary.singleUse ? null : beneficiary.id,
    });
    this.saveBeneficiary.emit({ clientId: this.form.controls.clientId.value, beneficiary });
    if (beneficiary.id === null && beneficiary.singleUse) {
      this.cancelEditBeneficiary();
    }
  }

  private filterClients(search: string = '') {
    if (!this.paymentClients) {
      return;
    }
    const searchString = search.toSearchString();
    if (searchString.length === 0) {
      this.filteredClients$.next(this.paymentClients.slice());
    } else {
      this.filteredClients$.next(this.paymentClients.filter(client =>
        `${client.firstName}${client.lastName}`.toSearchString().includes(searchString)
        || `${client.lastName}${client.firstName}`.toSearchString().includes(searchString),
      ));
    }
  }

  private filterAccounts(search: string = '') {
    if (!this.paymentAccounts) {
      return;
    }
    const accounts = this.form.controls.clientId.value === ''
      ? this.paymentAccounts.slice()
      : this.paymentAccounts
        .filter(account => account.clientId === this.form.controls.clientId.value)
        .sort((a, b) => a.position - b.position);
    const searchString = search.toSearchString();
    if (searchString.length === 0) {
      this.filteredAccounts$.next(accounts);
    } else {
      this.filteredAccounts$.next(accounts.filter(account =>
        account.iban.toSearchString().includes(searchString)
        || account.label?.toSearchString().includes(searchString),
      ));
    }
  }

  private filterBeneficiaries(search: string = '') {
    if (!this.beneficiaries) {
      return;
    }
    const searchString = search.toSearchString();
    if (searchString.length === 0) {
      this.filteredBeneficiaries$.next(this.beneficiaries.slice());
    } else {
      this.filteredBeneficiaries$.next(this.beneficiaries.filter(beneficiary =>
        beneficiary.counterpartReference?.toSearchString().includes(searchString)
        || beneficiary.counterpartName?.toSearchString().includes(searchString),
      ));
    }
  }

  private reloadBeneficiaries() {
    if (this.form.controls.beneficiary.value != null && !this.form.controls.beneficiary.value?.singleUse && !this.form.controls.beneficiary.value.global) {
      this.form.controls.beneficiary.setValue(null);
    }
    this.fetchClientBeneficiaries.emit(this.form.controls.clientId.value);
  }

  private selectFirstAccount() {
    this.form.controls.accountId.setValue(this.filteredAccounts$.value && this.filteredAccounts$.value.length > 0 ? this.filteredAccounts$.value[0].id : '');
  }

  private limitAmountToTwoDecimals() {
    const amount = this.form.controls.amount.value;
    if (amount === null) {
      return;
    }
    const twoDigitValue = floor(amount, 2);
    if (amount !== twoDigitValue) {
      this.form.controls.amount.setValue(twoDigitValue);
    }
  }
}
